import { onOnline, onOffline, onFocus, onFocusLost } from './setupListeners.js';
import { isQueryDefinition, isMutationDefinition } from '../endpointDefinitions.js';
import { buildThunks } from './buildThunks.js';
import { buildSelectors } from './buildSelectors.js';
import { buildSlice } from './buildSlice.js';
import { buildMiddleware } from './buildMiddleware.js';
import { buildInitiate } from './buildInitiate.js';
import { safeAssign } from '../tsHelpers.js';

/**
 * Note: this file should import all other files for type discovery and declaration merging
 */
const coreModuleName = Symbol();
const coreModule = () => ({
  name: coreModuleName,

  init(api, {
    baseQuery,
    entityTypes,
    reducerPath,
    serializeQueryArgs,
    keepUnusedDataFor,
    refetchOnMountOrArgChange,
    refetchOnFocus,
    refetchOnReconnect
  }, context) {

    const assertEntityType = entity => {
      if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
        if (!entityTypes.includes(entity.type)) {
          console.error(`Entity type '${entity.type}' was used, but not specified in \`entityTypes\`!`);
        }
      }

      return entity;
    };

    Object.assign(api, {
      reducerPath,
      endpoints: {},
      internalActions: {
        onOnline,
        onOffline,
        onFocus,
        onFocusLost
      },
      util: {}
    });
    const {
      queryThunk,
      mutationThunk,
      patchQueryResult,
      updateQueryResult,
      prefetchThunk,
      buildMatchThunkActions
    } = buildThunks({
      baseQuery,
      reducerPath,
      context,
      api,
      serializeQueryArgs
    });
    const {
      reducer,
      actions: sliceActions
    } = buildSlice({
      context,
      queryThunk,
      mutationThunk,
      reducerPath,
      assertEntityType,
      config: {
        refetchOnFocus,
        refetchOnReconnect,
        refetchOnMountOrArgChange,
        keepUnusedDataFor,
        reducerPath
      }
    });
    safeAssign(api.util, {
      patchQueryResult,
      updateQueryResult,
      prefetchThunk
    });
    safeAssign(api.internalActions, sliceActions);
    const {
      middleware
    } = buildMiddleware({
      reducerPath,
      context,
      queryThunk,
      mutationThunk,
      api,
      assertEntityType
    });
    safeAssign(api, {
      reducer: reducer,
      middleware
    });
    const {
      buildQuerySelector,
      buildMutationSelector
    } = buildSelectors({
      serializeQueryArgs: serializeQueryArgs,
      reducerPath
    });
    const {
      buildInitiateQuery,
      buildInitiateMutation
    } = buildInitiate({
      queryThunk,
      mutationThunk,
      api,
      serializeQueryArgs: serializeQueryArgs
    });
    return {
      name: coreModuleName,

      injectEndpoint(endpoint, definition) {
        var _anyApi$endpoints, _anyApi$endpoints$end;

        const anyApi = api;
        (_anyApi$endpoints$end = (_anyApi$endpoints = anyApi.endpoints)[endpoint]) != null ? _anyApi$endpoints$end : _anyApi$endpoints[endpoint] = {};

        if (isQueryDefinition(definition)) {
          safeAssign(anyApi.endpoints[endpoint], {
            select: buildQuerySelector(endpoint, definition),
            initiate: buildInitiateQuery(endpoint, definition)
          }, buildMatchThunkActions(queryThunk, endpoint));
        } else if (isMutationDefinition(definition)) {
          safeAssign(anyApi.endpoints[endpoint], {
            select: buildMutationSelector(),
            initiate: buildInitiateMutation(endpoint, definition)
          }, buildMatchThunkActions(mutationThunk, endpoint));
        }
      }

    };
  }

});

export { coreModule, coreModuleName };
//# sourceMappingURL=module.js.map
